import React from 'react';
import { Card, Progress, Divider, Avatar, Badge } from 'antd';
import { FlexBox, TextCommon } from '@esg/ui';

export interface DistributionItem {
    label: string;
    percent: number;
    strokeColor: string;
}

export interface ListItem {
    name: string;
    value: number;
    tagColor?: string;
}

export interface ConsumptionData {
    used: number;
    total: number;
    metricLabel: string;
    strokeColor: string;
}

export interface MetricChannel {
    name: string;
    value: number;
    percent: string;
}

export interface MetricsData {
    total: number;
    icon: React.ReactNode;
    iconBgColor: string;
    iconColor: string;
    channels: MetricChannel[];
}

export interface AnalyticsCardProps {
    title: string;
    type: 'distribution' | 'list' | 'consumption' | 'metrics';
    distributionItems?: DistributionItem[];
    listItems?: ListItem[];
    consumptionData?: ConsumptionData;
    metricsData?: MetricsData;
}

const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
    title,
    type,
    distributionItems,
    listItems,
    consumptionData,
    metricsData
}) => {

    const getTagColor = (tagColor?: string) => {
        switch (tagColor) {
            case 'green': return '#52c41a';
            case 'blue': return '#1890ff';
            case 'red': return '#ff4d4f';
            case 'purple': return '#722ed1';
            default: return '#d9d9d9';
        }
    };

    return (
        <Card title={title} size="small" style={{ height: '100%', borderRadius: 8, border: '1px solid #f0f0f0', backgroundColor: '#ffffff' }}>
            
            {/* Distribution */}
            {type === 'distribution' && distributionItems && (
                <FlexBox direction="column" gap={16} width="100%">
                    {distributionItems.map((item, idx) => (
                        <FlexBox key={idx} direction="column" gap={8} width="100%">
                            <FlexBox direction="row" justifyContent="space-between" alignItems="center" width="100%">
                                <FlexBox direction="row" alignItems="center" gap={8}>
                                    <Badge color={item.strokeColor} />
                                    <TextCommon fontSize={14} color="#595959">
                                        {item.label}
                                    </TextCommon>
                                </FlexBox>
                                <TextCommon fontSize={14} fontWeight={600} color="#262626">
                                    {item.percent}%
                                </TextCommon>
                            </FlexBox>
                            <Progress 
                                percent={item.percent} 
                                strokeColor={item.strokeColor} 
                                showInfo={false} 
                                size={['100%', 8]} 
                            />
                        </FlexBox>
                    ))}
                </FlexBox>
            )}

            {/* List Tracker */}
            {type === 'list' && listItems && (() => {
                const totalSum = listItems.reduce((acc, item) => acc + (item.value || 0), 0);

                return (
                    <FlexBox direction="column" gap={12} width="100%">
                        {listItems.map((item, idx) => {
                            const percentage = totalSum > 0 ? Math.round((item.value / totalSum) * 100) : 0;
                            const themeColor = getTagColor(item.tagColor);

                            return (
                                <FlexBox key={idx} direction="column" gap={4} width="100%">
                                    {/* FIX: Added width="100%" and gap={8} to separate the text and numbers */}
                                    <FlexBox direction="row" justifyContent="space-between" alignItems="center" width="100%" gap={8}>
                                        <TextCommon fontSize={12} color="#595959" fontWeight={500}>
                                            {item.name}
                                        </TextCommon>
                                        <FlexBox direction="row" gap={6} alignItems="center">
                                            <TextCommon fontSize={13} fontWeight={600} color="#262626">
                                                {item.value.toLocaleString()}
                                            </TextCommon>
                                            <TextCommon fontSize={11} color="#8c8c8c" fontWeight={400}>
                                                ({percentage}%)
                                            </TextCommon>
                                        </FlexBox>
                                    </FlexBox>
                                    <Progress percent={percentage} strokeColor={themeColor} showInfo={false} size={['100%', 6]} />
                                </FlexBox>
                            );
                        })}
                    </FlexBox>
                );
            })()}

            {/* Consumption Dashboard */}
            {type === 'consumption' && consumptionData && (
                <FlexBox direction="column" alignItems="center" gap={12} width="100%">
                    <Progress 
                        type="dashboard" 
                        percent={Math.round((consumptionData.used / consumptionData.total) * 100) || 0} 
                        size={120} 
                        strokeColor={consumptionData.strokeColor} 
                    />
                    <TextCommon fontSize={14} color="#595959">
                        {consumptionData.used.toLocaleString()} / {consumptionData.total.toLocaleString()} {consumptionData.metricLabel}
                    </TextCommon>
                </FlexBox>
            )}

            {/* Metrics Channel Card */}
            {type === 'metrics' && metricsData && (
                <FlexBox direction="column" gap={12} width="100%">
                    <FlexBox direction="row" justifyContent="space-between" alignItems="center" width="100%">
                        <TextCommon fontSize={22} fontWeight={700} color="#262626">
                            {metricsData.total.toLocaleString()}
                        </TextCommon>
                        
                        <Avatar 
                            size={36} 
                            style={{ backgroundColor: metricsData.iconBgColor, color: metricsData.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            {metricsData.icon}
                        </Avatar>
                    </FlexBox>
                    
                    <Divider style={{ margin: 0 }} />
                    
                    <FlexBox direction="column" gap={8} width="100%">
                        {metricsData.channels.map((ch, idx) => (
                            <FlexBox key={idx} direction="row" gap={4} alignItems="center" width="100%" justifyContent="space-between">
                                <TextCommon fontSize={14} color="#595959">
                                    {ch.name}:
                                </TextCommon>
                                <FlexBox direction="row" gap={4} alignItems="center">
                                    <TextCommon fontSize={14} fontWeight="bold" color="#595959">
                                        {ch.value.toLocaleString()}
                                    </TextCommon>
                                    <TextCommon fontSize={12} color="#8c8c8c">
                                        ({ch.percent}%)
                                    </TextCommon>
                                </FlexBox>
                            </FlexBox>
                        ))}
                    </FlexBox>
                </FlexBox>
            )}
        </Card>
    );
};

export { AnalyticsCard };